// @flow
import React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { makeStyles, useTheme } from '@material-ui/styles';
import Fab from '@material-ui/core/Fab';
import Input from '@material-ui/core/Input';
import Slide from '@material-ui/core/Slide';
import SendIcon from '@material-ui/icons/Send';

import TtsIcon from '../../../assets/icons/tts.svg';

import { scribeCreateTranscript, scribeDeselectTranscriptWord, startRecord, stopRecord } from '../../actions';

import { attemptSpeakWithVoice } from '../../utils/textToSpeech';

const getEditRight = (intercomDisplayed: boolean) => (intercomDisplayed ? 170 : 90);

const useStyles = makeStyles(theme => ({
  input: {
    position: 'absolute',
    bottom: 30,
    right: props => getEditRight(props.intercomDisplayed) + 70,
    width: props => `calc(100% - ${getEditRight(props.intercomDisplayed) + 90}px)`,
    fontSize: props => props.fontSize,
  },
  fab: {
    position: 'absolute',
    bottom: 30,
    color: theme.palette.ava.white,
    zIndex: 101,
  },
}));

interface Props {
  value: string;
  onChange: Function;
  keyboardInputShown: boolean;
  // $FlowFixMe
  ttsBestVoice: SpeechSynthesisVoice | typeof undefined;
  recording: boolean;
  user: Object;
  intercomDisplayed: boolean;
  fontSize: number;
  scribeCreateTranscript: Function;
  scribeDeselectTranscriptWord: Function;
  startRecord: Function;
  stopRecord: Function;
}

const KeyboardInput = ({
  value,
  onChange,
  keyboardInputShown,
  recording,
  ttsBestVoice,
  user,
  intercomDisplayed,
  fontSize,
  scribeCreateTranscript,
  scribeDeselectTranscriptWord,
  startRecord,
  stopRecord,
}: Props) => {
  const classes = useStyles({ intercomDisplayed, fontSize });
  const theme = useTheme();
  const { t } = useTranslation();

  const createNewTranscript = () => {
    onChange('');
    scribeDeselectTranscriptWord();
    scribeCreateTranscript(undefined, value, user.avaId);
    scribeDeselectTranscriptWord();
  };

  const onSubmit = event => {
    event.preventDefault();
    event.stopPropagation();
    createNewTranscript();
  };

  const onInputChange = event => {
    event.preventDefault();
    event.stopPropagation();
    scribeDeselectTranscriptWord();
    onChange(event.target.value);
  };

  const onKeyDown = event => {
    if (['Enter'].includes(event.key)) onSubmit(event);
  };

  const onSynthesizeSpeech = event => {
    event.preventDefault();
    event.stopPropagation();
    createNewTranscript();
    attemptSpeakWithVoice({
      message: value,
      recording,
      startRecord,
      stopRecord,
      voice: ttsBestVoice,
    });
  };

  return (
    <>
      {keyboardInputShown && (
        <form onSubmit={onSubmit}>
          <Slide direction="left" in mountOnEnter unmountOnExit>
            <Input
              classes={{ root: classes.input }}
              onChange={onInputChange}
              value={value}
              placeholder={t('conversation.typeTranscriptHere')}
              onKeyDown={onKeyDown}
              autoFocus
            />
          </Slide>
        </form>
      )}
      {keyboardInputShown && value && ttsBestVoice && (
        <Fab
          classes={{ root: classes.fab }}
          onClick={onSynthesizeSpeech}
          style={{
            right: getEditRight(intercomDisplayed),
            backgroundColor: theme.palette.ava.white,
          }}
        >
          <img width="30" src={TtsIcon} alt="Synthesize text" />
        </Fab>
      )}
      {keyboardInputShown && value && (
        <Fab
          classes={{ root: classes.fab }}
          onClick={createNewTranscript}
          style={{
            right: getEditRight(intercomDisplayed) - 70,
            backgroundColor: theme.palette.ava.blue,
          }}
        >
          <SendIcon />
        </Fab>
      )}
    </>
  );
};

const mapStateToProps = ({
  scribeConversation: {
    recording,
    ui: { keyboardInputShown },
  },
  scribeSession: { captioner, intercomDisplayed },
  scribeTranscriptUI: { fontSize },
}) => ({ recording, keyboardInputShown, user: captioner, intercomDisplayed, fontSize });

const mapDispatchToProps = {
  scribeCreateTranscript,
  scribeDeselectTranscriptWord,
  startRecord,
  stopRecord,
};

export default connect(mapStateToProps, mapDispatchToProps)(KeyboardInput);
